import { useEffect, useState } from 'preact/hooks'
import { contactDetailApi, type EmailEngagementDto, type ContactAnchorType } from '@/api/contactDetail'
import { relativeTime } from '../contactFormat'

interface Props {
  workspaceId: string
  campaignId: string
  anchorType: ContactAnchorType
  anchorId: string
}

export function ContactEmailsTab({ workspaceId, campaignId, anchorType, anchorId }: Props) {
  const [data, setData] = useState<EmailEngagementDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    contactDetailApi.getEmails(workspaceId, campaignId, anchorType, anchorId, ctrl.signal)
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setData(res.data)
        else setError(res.error || 'Failed to load emails')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, campaignId, anchorType, anchorId])

  if (loading && !data) return <div class="px-6 py-10 text-sm text-slate-500 dark:text-slate-400">Loading emails…</div>
  if (error && !data) return <div class="px-6 py-10 text-sm text-red-600 dark:text-red-400">{error}</div>
  if (!data || data.Items.length === 0) {
    return (
      <div class="px-6 py-16 text-center">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-200">No emails yet</p>
        <p class="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">Sends to this contact appear here with their status, opens, and clicks.</p>
      </div>
    )
  }

  const s = data.Summary
  return (
    <div class="px-6 py-6">
      {/* summary measure row */}
      <div class="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3">
        <Measure value={s.Sent} label="sent" />
        <Measure value={s.Delivered} label="delivered" />
        <Measure value={s.OpenRatePercent != null ? `${s.OpenRatePercent}%` : '—'} label="open rate" />
        <Measure value={s.Clicked} label="clicks" />
        {s.Bounced > 0 && <Measure value={s.Bounced} label="bounced" />}
      </div>

      <div class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-200 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <th class="px-4 py-2.5 font-semibold">Subject</th>
              <th class="px-4 py-2.5 font-semibold">Type</th>
              <th class="px-4 py-2.5 font-semibold">Status</th>
              <th class="hidden px-4 py-2.5 font-semibold sm:table-cell">Sent</th>
              <th class="px-4 py-2.5 font-semibold text-right">Opens</th>
              <th class="px-4 py-2.5 font-semibold text-right">Clicks</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800/70">
            {data.Items.map(item => (
              <tr key={item.Id} class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td class="px-4 py-2.5 text-slate-800 dark:text-slate-200">{item.Subject || '(no subject)'}</td>
                <td class="px-4 py-2.5">
                  <span class="inline-flex items-center rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">{item.EmailType}</span>
                </td>
                <td class="px-4 py-2.5"><StatusBadge status={item.Status} /></td>
                <td class="hidden px-4 py-2.5 text-slate-500 dark:text-slate-400 sm:table-cell">{relativeTime(item.SentAtUtc)}</td>
                <td class="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{item.OpenCount}</td>
                <td class="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{item.ClickCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Measure({ value, label }: { value: string | number; label: string }) {
  return (
    <div class="flex items-baseline gap-1.5">
      <span class="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{value}</span>
      <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone = (() => {
    const s = status.toLowerCase()
    if (s.includes('click') || s.includes('open')) return 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400'
    if (s.includes('deliver') || s === 'sent') return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
    if (s.includes('bounce') || s.includes('fail') || s.includes('complain')) return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
    if (s.includes('schedul') || s.includes('queue')) return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  })()
  return <span class={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{status}</span>
}
